import { useRef, useEffect, useState, useCallback } from 'react';
import { useDarkMode } from '../../context/DarkModeContext';

function resolveCollision(a, b) {
  const dx = b.x - a.x;
  const dy = b.y - a.y;
  const dist = Math.sqrt(dx * dx + dy * dy);
  const minDist = a.r + b.r;
  if (dist < minDist && dist > 0) {
    const nx = dx / dist;
    const ny = dy / dist;
    const overlap = minDist - dist;
    const totalMass = a.mass + b.mass;
    a.x -= nx * overlap * (b.mass / totalMass);
    a.y -= ny * overlap * (b.mass / totalMass);
    b.x += nx * overlap * (a.mass / totalMass);
    b.y += ny * overlap * (a.mass / totalMass);
    const dvx = a.vx - b.vx;
    const dvy = a.vy - b.vy;
    const dot = dvx * nx + dvy * ny;
    if (dot > 0) {
      const impulse = (2 * dot) / totalMass;
      a.vx -= impulse * b.mass * nx;
      a.vy -= impulse * b.mass * ny;
      b.vx += impulse * a.mass * nx;
      b.vy += impulse * a.mass * ny;
    }
  }
}

export default function BrownianMotionViz() {
  const { dark } = useDarkMode();
  const canvasRef = useRef(null);
  const simRef    = useRef(null);
  const rafRef    = useRef(null);
  const [nParticles, setNParticles] = useState(40);
  const [speed, setSpeed]           = useState(2);

  const init = useCallback((n, spd, canvas) => {
    if (!canvas) return;
    const W = canvas.offsetWidth || 600;
    const H = 400;
    canvas.width  = W;
    canvas.height = H;
    const big = { x: W / 2, y: H / 2, r: 22, vx: 0, vy: 0, mass: 20 };
    const particles = [];
    for (let i = 0; i < n; i++) {
      const angle = Math.random() * Math.PI * 2;
      const dist  = 60 + Math.random() * (Math.min(W, H) / 2 - 80);
      particles.push({
        x:    W / 2 + Math.cos(angle) * dist,
        y:    H / 2 + Math.sin(angle) * dist,
        r:    5 + Math.random() * 3,
        vx:   (Math.random() - 0.5) * spd * 3,
        vy:   (Math.random() - 0.5) * spd * 3,
        mass: 1,
      });
    }
    simRef.current = { big, particles, trail: [], W, H };
  }, []);

  // Main animation loop — runs once, reads simRef which is always current
  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    const ctx = canvas.getContext('2d');

    init(nParticles, speed, canvas);

    function step() {
      const { big, particles, trail } = simRef.current;
      const { W, H } = simRef.current;
      for (const p of particles) {
        p.x += p.vx; p.y += p.vy;
        if (p.x - p.r < 0) { p.x = p.r; p.vx *= -1; }
        if (p.x + p.r > W) { p.x = W - p.r; p.vx *= -1; }
        if (p.y - p.r < 0) { p.y = p.r; p.vy *= -1; }
        if (p.y + p.r > H) { p.y = H - p.r; p.vy *= -1; }
        resolveCollision(big, p);
      }
      big.x += big.vx; big.y += big.vy;
      if (big.x - big.r < 0) { big.x = big.r; big.vx *= -1; }
      if (big.x + big.r > W) { big.x = W - big.r; big.vx *= -1; }
      if (big.y - big.r < 0) { big.y = big.r; big.vy *= -1; }
      if (big.y + big.r > H) { big.y = H - big.r; big.vy *= -1; }
      if (
        trail.length === 0 ||
        Math.hypot(big.x - trail[trail.length - 1].x, big.y - trail[trail.length - 1].y) > 3
      ) {
        trail.push({ x: big.x, y: big.y });
      }
      if (trail.length > 600) trail.shift();
    }

    function draw() {
      const { big, particles, trail, W, H } = simRef.current;
      const BIG_COLOR    = '#016ea9';
      const SMALL_FILL   = dark ? '#014265' : '#bae6fd';
      const SMALL_STROKE = dark ? '#29b0e8' : '#016ea9';
      const TRAIL_COLOR  = dark ? '#29b0e8' : '#015887';
      const BG           = dark ? '#161616' : 'rgba(245,247,241,1)';

      ctx.clearRect(0, 0, W, H);
      ctx.fillStyle = BG;
      ctx.fillRect(0, 0, W, H);

      if (trail.length > 1) {
        ctx.beginPath();
        ctx.moveTo(trail[0].x, trail[0].y);
        for (let i = 1; i < trail.length; i++) ctx.lineTo(trail[i].x, trail[i].y);
        ctx.strokeStyle   = TRAIL_COLOR;
        ctx.lineWidth     = 1.5;
        ctx.globalAlpha   = 0.5;
        ctx.stroke();
        ctx.globalAlpha   = 1;
      }

      for (const p of particles) {
        ctx.beginPath();
        ctx.arc(p.x, p.y, p.r, 0, Math.PI * 2);
        ctx.fillStyle   = SMALL_FILL;
        ctx.strokeStyle = SMALL_STROKE;
        ctx.lineWidth   = 1;
        ctx.fill();
        ctx.stroke();
      }

      ctx.beginPath();
      ctx.arc(big.x, big.y, big.r, 0, Math.PI * 2);
      ctx.fillStyle   = BIG_COLOR;
      ctx.strokeStyle = '#0C447C';
      ctx.lineWidth   = 2;
      ctx.fill();
      ctx.stroke();
    }

    function loop() {
      for (let i = 0; i < 3; i++) step();
      draw();
      rafRef.current = requestAnimationFrame(loop);
    }

    function handleResize() {
      init(nParticles, speed, canvas);
    }
    window.addEventListener('resize', handleResize);
    rafRef.current = requestAnimationFrame(loop);

    return () => {
      cancelAnimationFrame(rafRef.current);
      window.removeEventListener('resize', handleResize);
    };
  }, [dark]); // restart loop when dark mode changes (colors update)

  // Re-init simulation when sliders change (loop keeps running)
  useEffect(() => {
    if (canvasRef.current) init(nParticles, speed, canvasRef.current);
  }, [nParticles, speed, init]);

  const labelCls = 'text-xs text-neutral-500 dark:text-neutral-400';
  const btnCls   = 'text-xs px-3 py-1.5 rounded-clay-sm border border-surface bg-surface text-neutral-700 dark:text-neutral-300 shadow-clay dark:shadow-clay-dark hover:shadow-clay-hover dark:hover:shadow-clay-dark-hover transition-all';

  return (
    <div className="my-6 rounded-clay border border-surface shadow-clay dark:shadow-clay-dark overflow-hidden">
      <canvas
        ref={canvasRef}
        height={400}
        style={{ display: 'block', width: '100%' }}
      />
      <div className="flex flex-wrap items-center gap-4 px-4 py-3 border-t border-neutral-100 dark:border-transparent bg-surface">
        <button className={btnCls} onClick={() => init(nParticles, speed, canvasRef.current)}>
          Reset
        </button>
        <label className={`flex items-center gap-2 ${labelCls}`}>
          Particles
          <input
            type="range" min="20" max="80" value={nParticles}
            onChange={e => setNParticles(Number(e.target.value))}
            className="w-24 accent-primary-600"
          />
          <span className="w-6 tabular-nums">{nParticles}</span>
        </label>
        <label className={`flex items-center gap-2 ${labelCls}`}>
          Speed
          <input
            type="range" min="1" max="5" value={speed}
            onChange={e => setSpeed(Number(e.target.value))}
            className="w-20 accent-primary-600"
          />
          <span className="w-4 tabular-nums">{speed}</span>
        </label>
      </div>
    </div>
  );
}
